import React, { useEffect, useState } from "react";
import { Icon, Checkbox, Header, Popup } from "semantic-ui-react";
import { Table, AvatarGroup, ControlledPopup } from "dyl-components";

function ActionBar({
    item,
    onTaskComplete,
    onTaskRemove,
    onTaskCall,
    hasEmailIntegrations,
    onTaskEmailNoIntegrationsContent,
    onSequenceTaskComplete,
    onSequenceTaskRemove,
    onSequenceTaskCall,
    onSequenceTaskEmail,
    onRefresh,
    setContactId,
    onSequenceTaskReschedule,
    isPhoneSystemSupported,
    onCallActivityContent,
    TextComponent,
    onSequenceTaskEmailSend
}) {
    const { onRemove, onComplete, onCall, onEmail } = {
        Task: {
            onRemove: onTaskRemove,
            onComplete: onTaskComplete,
            onCall: onTaskCall
        },
        SequenceTask: {
            onRemove: onSequenceTaskRemove,
            onComplete: onSequenceTaskComplete,
            onCall: onSequenceTaskCall,
            onEmail: onSequenceTaskEmail,
        },
    }[item.activity_type];

    const getIconClassName = (action) => {
        const actionText = action.split(" ")[0];
        switch (actionText) {
            case "Call":
                return "fa-solid fa-phone";
            case "Email":
                return "fa-solid fa-envelope";
            case "Text":
                return "fa-solid fa-comments";
            default:
                return "";
        }
    };
    const [isCallActivityModalOpen, setIsCallActivityModalOpen] = useState(false);

    const hasPhone = item?.contacts?.some?.(
        (p) => p?.phone && p?.phone?.trim?.()?.length > 0,
    );

    const hasEmail = item?.contacts?.email?.some?.(
        (e) => e?.email && e?.email?.trim?.()?.length > 0,
    );

    useEffect(() => {
        const canEmail =
            item.contacts &&
            item.contacts.length > 0 &&
            Boolean(item.contacts[0].email);
        if (hasEmailIntegrations && canEmail) {
            setContactId(item.contacts[0].id);
        }
    }, [item, hasEmailIntegrations, setContactId]);

    const getEmailTaskContent = () => {
        const canEmail =
            item.contacts &&
            item.contacts.length > 0 &&
            Boolean(item.contacts[0].email);
        if (hasEmailIntegrations && canEmail) {
            return item?.activity_type === "SequenceTask" ? (
                <ControlledPopup
                    header={<Header as='h4' textAlign='center'>
                        Send Email?
                    </Header>}
                    trigger={<Icon
                        size="large"
                        link
                        className={"fa-solid fa-envelope"}
                        color="primary"
                        style={{ marginTop: 2 }}
                    />}
                    onConfirm={() => { onSequenceTaskEmailSend(item); }}
                    inverted
                    position="bottom center"
                />

            ) : (
                <Icon
                    size="large"
                    link
                    className={"fa-solid fa-envelope"}
                    color="primary"
                    style={{ marginTop: 2 }}
                    onClick={onEmail}
                    disabled={!hasEmail}
                />
            );
        }
        return (
            <Popup
                trigger={
                    <span style={{ cursor: !hasEmailIntegrations ? "help" : "default" }}>
                        <Icon
                            size="large"
                            link
                            className={"fa-solid fa-envelope"}
                            color="primary"
                            style={{ marginTop: 2 }}
                            disabled
                        />
                    </span>
                }
                content={!hasEmailIntegrations ? onTaskEmailNoIntegrationsContent() : "No email found for contact"}
                on={!hasEmailIntegrations ? "click" : "hover"}
                position="bottom right"
                pinned
                basic
                wide
            />
        );
    };

    const getActionButton = (action, isComplete) => {
        if (action && action !== "To-Do") {
            if (action === "Email") {
                return getEmailTaskContent();
            }
            if (action === "Call" && !isPhoneSystemSupported) {
                return (
                    <Popup
                        trigger={
                            <Icon
                                size="large"
                                link
                                className={`${getIconClassName(action)}`}
                                color="primary"
                                style={{ marginTop: 2 }}
                                disabled={!hasPhone}
                            />
                        }
                        content={onCallActivityContent?.(
                            item.contacts[0],
                            () => setIsCallActivityModalOpen(false)
                        )}
                        open={isCallActivityModalOpen}
                        onOpen={() => setIsCallActivityModalOpen(true)}
                        onClose={() => setIsCallActivityModalOpen(false)}
                        closeOnDocumentClick={false}
                        on="click"
                        position="bottom right"
                        pinned
                        basic
                        wide
                    />
                );
            }
            if (action === "Text") {
                return (
                    <TextComponent id={item.id} phone={item?.contacts[0]?.phone} contact_id={item?.contacts[0]?.id} isComplete={isComplete} isSequenceTask={item.activity_type === "SequenceTask"} />
                );
            }
            return (
                <Icon
                    size="large"
                    link
                    className={`${getIconClassName(action)}`}
                    color="primary"
                    style={{ marginTop: 2 }}
                    onClick={
                        action === "Call" ? () => {onCall(item);} : () => {}
                    }
                    disabled={(action === "Call" && !hasPhone)}
                />
            );
        }
        return null;
    };
    const isComplete = item.status?.toLowerCase()?.includes("complete");
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "row",
                gap: 10,
                alignItems: "center",
                justifyContent: "left",
                paddingLeft: "20px",
            }}
        >
            <Checkbox
                className="SequenceTime__taskCheckbox"
                style={{ marginRight: 6 }}
                checked={isComplete}
                disabled={isComplete || !hasPhone || !item?.contacts?.[0]?.email}
                onClick={() => {
                    if (!isComplete) {
                        onComplete(item.id, item.action);
                    }
                }}
            />
            {getActionButton(item.action, isComplete)}
            {item?.activity_type === "SequenceTask" ? <Icon
                size="large"
                link
                className="fa-solid fa-calendar"
                color="primary"
                style={{ marginTop: 2 }}
                onClick={() => { onSequenceTaskReschedule(item.id) }}
                disabled={isComplete}
            /> : null}
            <ControlledPopup
                header={
                    <Header as="h4" textAlign="center">
                        Are you sure?
                    </Header>
                }
                trigger={
                    <Icon
                        size="big"
                        link
                        className="fa-solid fa-xmark redIcon"
                        disabled={isComplete}
                    />
                }
                onConfirm={() => {
                    onRemove(item.id, onRefresh);
                }}
                disabled={isComplete}
                position={"top center"}
            />
        </div>
    );
}

function EventRow({
    item,
    onEventClick,
    onContactClick,
    onTaskComplete,
    onTaskRemove,
    onTaskCall,
    hasEmailIntegrations,
    onTaskEmailNoIntegrationsContent,
    onSequenceTaskComplete,
    onSequenceTaskRemove,
    onSequenceTaskCall,
    onSequenceTaskEmail,
    onRefresh,
    setContactId,
    onSequenceTaskReschedule,
    isPhoneSystemSupported,
    onCallActivityContent,
    TextComponent,
    onSequenceTaskEmailSend,
    onSequenceTaskText
}) {
    function getStatusIcon({ status, source, type, isConfirmed = true }) {
        // status is 'Pending' or 'Completed'
        // source is 'System' or 'Integrated'
        // type is 'Event', 'Task' or 'SequenceTask'
        return (
            <Icon
                name={`circle ${isConfirmed ? "" : "outline"}`}
                className={`
                IconStatus
                IconStatus__${type} 
                IconStatus__${type}__${status}
                IconStatus__${type}__${source}
            `}
            />
        );
    }

    return (
        <React.Fragment>
            <Table.Cell />
            <Table.Cell>
                {getStatusIcon({
                    status: item.status,
                    source: item.source,
                    type: item.activity_type,
                })}
                {item.allDay ? "" : item?.hour}
            </Table.Cell>
            <Table.Cell>
                <b>{item?.action}</b>
                <br />
                <span {...(item?.status === 'Overdue' && { className: "EventStatus--Overdue" })}>{item?.status}</span>
            </Table.Cell>
            <Table.Cell>
                <b>{item?.name}</b>
                <br />
                <a
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                        if (item?.activity_type !== "SequenceTask") {
                            onEventClick(item.id)
                        }
                        else {
                            const canEmail =
                                item.contacts &&
                                item.contacts.length > 0 &&
                                Boolean(item.contacts[0].email);
                            if (canEmail && item?.action === "Email") {
                                onSequenceTaskEmail(item)
                            }
                            if (item?.action === "Text") {
                               onSequenceTaskText(item)
                            }
                        }
                    }}
                >
                    <b>{item?.label}</b>
                </a>
            </Table.Cell>
            <Table.Cell>{item.notes || "No notes"}</Table.Cell>
            <Table.Cell style={{ "text-align": "center" }}>
                <AvatarGroup
                    persons={item?.contacts?.map((contact) => ({
                        ...contact,
                        first_name: contact?.first_name || contact?.name,
                        last_name: contact?.last_name || "",
                    }))}
                    size={"3em"}
                    onClick={onContactClick}
                />
            </Table.Cell>
            <Table.Cell style={{ "text-align": "center" }}>
                {(item?.activity_type === "Task" ||
                    item?.activity_type === "SequenceTask") && (
                        <ActionBar
                            item={item}
                            onTaskComplete={onTaskComplete}
                            onTaskRemove={onTaskRemove}
                            onTaskCall={onTaskCall}
                            hasEmailIntegrations={hasEmailIntegrations}
                            onTaskEmailNoIntegrationsContent={
                                onTaskEmailNoIntegrationsContent
                            }
                            onSequenceTaskComplete={onSequenceTaskComplete}
                            onSequenceTaskRemove={onSequenceTaskRemove}
                            onSequenceTaskCall={onSequenceTaskCall}
                            onSequenceTaskEmail={onSequenceTaskEmail}
                            onRefresh={onRefresh}
                            setContactId={setContactId}
                            onSequenceTaskReschedule={onSequenceTaskReschedule}
                            onCallActivityContent={onCallActivityContent}
                            isPhoneSystemSupported={isPhoneSystemSupported}
                            TextComponent={TextComponent}
                            onSequenceTaskEmailSend={onSequenceTaskEmailSend}
                        />
                )}
            </Table.Cell>
        </React.Fragment>
    );
}

function CollapsibleRow({ date, items, goToDay }) {
    const [isRowCollapsed, setIsRowCollapsed] = useState(false);
    let isDisabled = items.length <= 0;
    if (isDisabled) {
        return (
            <Table.Row disabled={isDisabled}>
                <Table.Cell>
                    <Icon name="chevron circle down" />
                </Table.Cell>
                <Table.Cell
                    className={`DateGroupHeader${isDisabled ? "__disabled" : ""
                        }`}
                >
                    {date}
                </Table.Cell>
                <Table.Cell />
                <Table.Cell />
                <Table.Cell />
            </Table.Row>
        );
    }

    return (
        <Table.CollapsibleRow
            disabled={isDisabled}
            collapsed={isRowCollapsed}
            onToggle={() => {
                setIsRowCollapsed(!isRowCollapsed);
            }}
            subrowContent={items}
            children={[
                <Table.Row disabled={isDisabled}>
                    <Table.Cell onClick={goToDay} className="DateGroupHeader">
                        {date}
                    </Table.Cell>
                    <Table.Cell />
                    <Table.Cell />
                    <Table.Cell />
                    <Table.Cell />
                </Table.Row>,
            ]}
        />
    );
}

export default function Rows({
    isGroupedByDate,
    items,
    goToDay,
    onEventClick,
    onContactClick,
    onTaskComplete,
    onTaskRemove,
    onTaskCall,
    hasEmailIntegrations,
    onTaskEmailNoIntegrationsContent,
    onSequenceTaskComplete,
    onSequenceTaskRemove,
    onSequenceTaskCall,
    onSequenceTaskEmail,
    onRefresh,
    setContactId,
    onSequenceTaskReschedule,
    onCallActivityContent,
    isPhoneSystemSupported,
    TextComponent,
    onSequenceTaskEmailSend,
    onSequenceTaskText
}) {
    return isGroupedByDate
        ? Object.entries(items).map(([date, { ts, events }]) => (
            <CollapsibleRow
                date={date}
                items={events.map((item) => (
                    <Table.CollapsibleRowContent key={item.id}>
                        <EventRow
                            item={item}
                            onEventClick={onEventClick}
                            onContactClick={onContactClick}
                            onTaskComplete={onTaskComplete}
                            onTaskRemove={onTaskRemove}
                            onTaskCall={onTaskCall}
                            hasEmailIntegrations={hasEmailIntegrations}
                            onTaskEmailNoIntegrationsContent={
                                onTaskEmailNoIntegrationsContent
                            }
                            onSequenceTaskComplete={onSequenceTaskComplete}
                            onSequenceTaskRemove={onSequenceTaskRemove}
                            onSequenceTaskCall={onSequenceTaskCall}
                            onSequenceTaskEmail={onSequenceTaskEmail}
                            onRefresh={onRefresh}
                            setContactId={setContactId}
                            onSequenceTaskReschedule={onSequenceTaskReschedule}
                            onCallActivityContent={onCallActivityContent}
                            isPhoneSystemSupported={isPhoneSystemSupported}
                            TextComponent={TextComponent}
                            onSequenceTaskEmailSend={onSequenceTaskEmailSend}
                            onSequenceTaskText={onSequenceTaskText}
                        />
                    </Table.CollapsibleRowContent>
                ))}
                onEventClick={onEventClick}
                onContactClick={onContactClick}
                goToDay={() => {
                    goToDay(ts);
                }}
                onTaskComplete={onTaskComplete}
                onTaskRemove={onTaskRemove}
                onTaskCall={onTaskCall}
                hasEmailIntegrations={hasEmailIntegrations}
                onTaskEmailNoIntegrationsContent={
                    onTaskEmailNoIntegrationsContent
                }
                onSequenceTaskComplete={onSequenceTaskComplete}
                onSequenceTaskRemove={onSequenceTaskRemove}
                onSequenceTaskCall={onSequenceTaskCall}
                onSequenceTaskEmail={onSequenceTaskEmail}
                onRefresh={onRefresh}
                setContactId={setContactId}
            />
        ))
        : items.map((item) => (
            <Table.Row key={item.id}>
                <EventRow
                    item={item}
                    onEventClick={onEventClick}
                    onTaskComplete={onTaskComplete}
                    onTaskRemove={onTaskRemove}
                    onTaskCall={onTaskCall}
                    hasEmailIntegrations={hasEmailIntegrations}
                    onTaskEmailNoIntegrationsContent={
                        onTaskEmailNoIntegrationsContent
                    }
                    onSequenceTaskComplete={onSequenceTaskComplete}
                    onSequenceTaskRemove={onSequenceTaskRemove}
                    onSequenceTaskCall={onSequenceTaskCall}
                    onSequenceTaskEmail={onSequenceTaskEmail}
                    onRefresh={onRefresh}
                    setContactId={setContactId}
                    onSequenceTaskReschedule={onSequenceTaskReschedule}
                    onContactClick={onContactClick}
                    onCallActivityContent={onCallActivityContent}
                    isPhoneSystemSupported={isPhoneSystemSupported}
                    TextComponent={TextComponent}
                />
            </Table.Row>
        ));
}
